import { FunctionComponent, JSX } from 'react'
import { OccasionalSlot } from '@/types/Slots'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import { useTranslation } from 'react-i18next'
import moment from 'moment/moment'
import { defineAvailableSlots, TimBarSlots } from '../../TimBarSlots/defineSlots'
import styles from '../DraftingStaffSlots/draftingSlots.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { EventBusy, LockOutlined, Schedule } from '@mui/icons-material'
import TimeBarSlots from '../../TimBarSlots/timeBarSlots'
import { getOccasionalSlotsIconTextToJSX } from '@/modules/helper/iconsHelper'

interface DraftingAvailableSlotsProps {
    availableSlot: OccasionalSlot
    opening: string
    closing: string
    setSelectedEvent: (event: EventCalendar | null) => void
    setShowSlotDialogOpen: (open: boolean) => void
}

const DraftingAvailableSlots: FunctionComponent<DraftingAvailableSlotsProps> = ({
    availableSlot,
    opening,
    closing,
    setSelectedEvent,
    setShowSlotDialogOpen
}: DraftingAvailableSlotsProps) => {
    const { t } = useTranslation()

    const slotHours: number = moment(availableSlot.end, 'HH:mm').diff(moment(availableSlot.start, 'HH:mm'), 'hours')

    const totalMinutes: number = moment.duration(moment(closing, 'HH:mm').diff(moment(opening, 'HH:mm'))).asMinutes()

    const timBarSlots: TimBarSlots[] = defineAvailableSlots(availableSlot, opening, closing)

    function handleSelectSlot(slot: TimBarSlots): void {
        const id: number | undefined = slot.id
        if (id) {
            setSelectedEvent({
                title: availableSlot.start + ' - ' + availableSlot.end,
                start: moment(availableSlot.start, 'HH:mm').toDate(),
                end: moment(availableSlot.end, 'HH:mm').toDate(),
                number: '',
                type: availableSlot.type,
                slots: [availableSlot],
                placeId: availableSlot.placeId ? availableSlot.placeId : undefined,
                sectionId: availableSlot.sectionId ? availableSlot.sectionId : undefined
            })
            setShowSlotDialogOpen(true)
        }
    }

    function getIcon(): JSX.Element {
        if (availableSlot.externalIcon) {
            return getOccasionalSlotsIconTextToJSX(availableSlot.externalIcon)
        }

        switch (availableSlot.type) {
            case CalendarEventType.EXPIRED:
            case CalendarEventType.REMAINDER_AVAILABLE:
                return <EventBusy />
            case CalendarEventType.SCHEDULED:
                return <LockOutlined />
            default:
                return <Schedule />
        }
    }

    return (
        <>
            <div className={styles.Line}>
                <div className={styles.StaffProfile}>
                    <ButtonIcon icon={getIcon()} className={styles.Icon} />
                    <div className={styles.Info}>
                        <span className={styles.Name}>
                            {availableSlot.section} {availableSlot.placeName ? `- ${availableSlot.placeName}` : ''}
                        </span>
                        <span className={styles.Hours}>
                            {slotHours} {slotHours > 1 ? t('hours') : t('hour')}
                        </span>
                    </div>
                </div>
                <div className={styles.Slots}>
                    <TimeBarSlots
                        timBarSlots={timBarSlots}
                        totalMinutes={totalMinutes}
                        handleSelect={handleSelectSlot}
                    />
                </div>
            </div>
        </>
    )
}

export default DraftingAvailableSlots
